import {
  dec,
  decimalPlaces,
  divRound,
  fromInt,
  isInteger,
  mul,
  mulRound,
  parse,
  prorate,
  round,
  sum,
  toFixed,
  toPlain,
  ZERO,
  type Dec,
} from './decimal';

/**
 * Live document totals for the quotation line editor — the same rules as the backend's totals engine
 * (ACCOUNTING_RULES §1 and §3, `modules/documents/totals.ts`):
 *
 * per line: gross = round(qty × unit price); the line discount (amount, or % of gross, rounded) comes off;
 * the document discount (amount, or % of the discounted lines) is prorated over the lines by the largest
 * remainder; tax per line on what remains — exclusive `tax = round(net × rate)`, inclusive
 * `net = round(amount / (1 + rate))`, `tax = amount − net`. Every amount is rounded at line level with
 * ROUND_HALF_UP; document totals are plain sums.
 *
 * Unlike the server it never throws: invalid input is reported per field so the editor can show it,
 * and the offending part counts as zero.
 */

export type DiscountType = 'AMOUNT' | 'PERCENT';

export interface DiscountInput {
  type: DiscountType;
  value: string;
}

export interface TotalsLine {
  /** Quantity in the entered unit. */
  qty: string;
  /** Base units per entered unit ("1" for the base unit). */
  factor: string;
  /** Unknown while the item is loading: then the whole-number check is skipped. */
  baseAllowsFraction?: boolean;
  /** Price per entered unit. */
  unitPrice: string;
  discount?: DiscountInput | null;
  /** Percent, e.g. "17". Ignored when tax is off. */
  taxRate?: string | null;
}

export interface TotalsOptions {
  decimals: number;
  taxEnabled: boolean;
  pricesIncludeTax: boolean;
  documentDiscount?: DiscountInput | null;
}

export type LineField = 'qty' | 'unitPrice' | 'discount';

export interface LineResult {
  qtyBase: string;
  gross: string;
  lineDiscount: string;
  documentDiscount: string;
  net: string;
  taxRate: string;
  tax: string;
  total: string;
  errors: Partial<Record<LineField, string>>;
}

export interface QuoteTotals {
  lines: LineResult[];
  subtotal: string;
  documentDiscount: string;
  discountTotal: string;
  netTotal: string;
  taxTotal: string;
  total: string;
  documentDiscountError?: string;
  /** True when any line or the document discount has an error. */
  hasErrors: boolean;
}

const HUNDRED = fromInt(100);
const QTY_SCALE = 4;
const PRICE_SCALE = 4;

/** Validation shared by the editor's fields (API: up to 15 digits, at most 4 decimals). */
export function checkAmount(value: string, label: string, scale = PRICE_SCALE): string | undefined {
  const v = value.trim();
  if (v === '') return `Enter ${label}.`;
  if (!/^\d{1,15}(\.\d+)?$/.test(v)) return `Enter a positive number.`;
  if (decimalPlaces(v) > scale) return `At most ${String(scale)} decimals.`;
  return undefined;
}

function discountAmount(
  discount: DiscountInput | null | undefined,
  base: Dec,
  decimals: number,
): { amount: Dec; error?: string } {
  if (!discount || discount.value.trim() === '') return { amount: ZERO };
  const error = checkAmount(discount.value, 'a discount');
  if (error) return { amount: ZERO, error };
  const value = dec(discount.value);
  let amount: Dec;
  if (discount.type === 'PERCENT') {
    if (value > HUNDRED) return { amount: ZERO, error: 'A percentage is at most 100.' };
    amount = divRound(mul(base, value), HUNDRED, decimals);
  } else {
    if (decimalPlaces(discount.value) > decimals)
      return { amount: ZERO, error: `At most ${String(decimals)} decimals.` };
    amount = value;
  }
  if (amount > base) return { amount: ZERO, error: 'The discount is larger than the amount.' };
  return { amount };
}

export function computeQuoteTotals(lines: TotalsLine[], options: TotalsOptions): QuoteTotals {
  const { decimals } = options;
  const fmt = (d: Dec) => toFixed(d, decimals);

  const first = lines.map((line) => {
    const errors: LineResult['errors'] = {};
    let qty = ZERO;
    const qtyError = checkAmount(line.qty, 'a quantity', QTY_SCALE);
    if (qtyError) errors.qty = qtyError;
    else {
      qty = dec(line.qty);
      if (qty === ZERO) errors.qty = 'Must be greater than zero.';
    }
    const factor = parse(line.factor) ?? fromInt(1);
    const qtyBase = mul(qty, factor);
    if (!errors.qty && line.baseAllowsFraction === false && !isInteger(qtyBase))
      errors.qty = 'Whole units only for this item.';

    let price = ZERO;
    const priceError = checkAmount(line.unitPrice, 'a price');
    if (priceError) errors.unitPrice = priceError;
    else price = dec(line.unitPrice);

    const gross = errors.qty ? ZERO : mulRound(qty, price, decimals);
    const d = discountAmount(line.discount, gross, decimals);
    if (d.error) errors.discount = d.error;
    return { line, qtyBase, gross, lineDiscount: d.amount, afterLine: gross - d.amount, errors };
  });

  const discountBase = sum(first.map((l) => l.afterLine));
  const doc = discountAmount(options.documentDiscount, discountBase, decimals);
  const shares = prorate(
    doc.amount,
    first.map((l) => l.afterLine),
    decimals,
  );

  const results = first.map((l, i) => {
    const share = shares[i] ?? ZERO;
    const amount = l.afterLine - share;
    const taxRate = options.taxEnabled ? (parse(l.line.taxRate ?? '0') ?? ZERO) : ZERO;
    let net: Dec;
    let tax: Dec;
    if (options.pricesIncludeTax) {
      // amount / (1 + r/100) = amount·100 / (100 + r)
      net = divRound(mul(amount, HUNDRED), HUNDRED + taxRate, decimals);
      tax = amount - net;
    } else {
      net = amount;
      tax = divRound(mul(net, taxRate), HUNDRED, decimals);
    }
    return {
      raw: { gross: l.gross, lineDiscount: l.lineDiscount, share, net, tax, total: net + tax },
      out: {
        qtyBase: toPlain(l.qtyBase),
        gross: fmt(l.gross),
        lineDiscount: fmt(l.lineDiscount),
        documentDiscount: fmt(share),
        net: fmt(net),
        taxRate: toFixed(taxRate, 4),
        tax: fmt(tax),
        total: fmt(net + tax),
        errors: l.errors,
      } satisfies LineResult,
    };
  });

  const raw = results.map((r) => r.raw);
  const hasErrors = doc.error !== undefined || first.some((l) => Object.keys(l.errors).length > 0);
  return {
    lines: results.map((r) => r.out),
    subtotal: fmt(sum(raw.map((r) => r.gross))),
    documentDiscount: fmt(doc.amount),
    discountTotal: fmt(sum(raw.map((r) => r.lineDiscount + r.share))),
    netTotal: fmt(sum(raw.map((r) => r.net))),
    taxTotal: fmt(sum(raw.map((r) => r.tax))),
    total: fmt(sum(raw.map((r) => r.total))),
    ...(doc.error ? { documentDiscountError: doc.error } : {}),
    hasErrors,
  };
}

/** List price per entered unit: sale price × unit factor, kept at 4 decimals like the API. */
export function listPrice(salePrice: string, factor: string): string {
  return toPlain(round(mul(dec(salePrice), parse(factor) ?? fromInt(1)), PRICE_SCALE));
}

/** True when the entered price is below the list price (needs sales.discount on the server). */
export function belowList(unitPrice: string, list: string): boolean {
  const p = parse(unitPrice);
  return p !== null && p < dec(list);
}
